import type { TagToggleProps } from './tagToggle.types'

import { styled, useStyleConfig, useTh, vui } from '../core'
import Icon from '../icon'
import T from '../t'
import { tagPillBaseCss } from '../tag/tagPillBase.css'
import { cs, isString } from '../utils'

// ---------------------------------------------------------------------------
// Styled primitive
// ---------------------------------------------------------------------------

const TagToggleBase = styled.buttonBox`
  ${tagPillBaseCss}

  cursor: pointer;
  user-select: none;
`

// ---------------------------------------------------------------------------
// TagToggle component
// ---------------------------------------------------------------------------

/**
 * A toggle button styled as a tag. Uses brand-only colour tokens and an
 * `aria-pressed` attribute to convey selected state.
 */
export const TagToggle = vui<'button', TagToggleProps>((props, ref) => {
  const { className, icon, isSelected = false, label, onChange, variant = 'solid', ...rest } = props

  const styles = useStyleConfig('TagToggle', { ...props, variant })
  const { selected, ...container } = styles.container ?? {}
  const {
    h,
    fontSize: themeFontSize,
    paddingX = 0,
    paddingY = 0,
    gapX = 0,
    borderRadius,
    bgHover,
    bgPressed,
    ...containerColors
  } = isSelected ? { ...container, ...selected } : container
  const iconStyles = styles.icon ?? {}

  // Resolved from the active theme (respects theme overrides) rather than a static import.
  const resolvedFontSize = useTh(`fontSizes.${themeFontSize}`, themeFontSize)
  const fontSize = `var(--vui-tag-font-size, ${resolvedFontSize}px)`

  // Theme values are literal pixels, not spacing-scale steps, so convert explicitly before
  // handing them to the `px`/`py`/`gap` system props.
  const toPx = (n: number) => `${n}px`

  return (
    <TagToggleBase
      aria-pressed={isSelected}
      borderRadius={borderRadius}
      className={cs('vui-tagToggle', className)}
      focusRing={2}
      fontSize={fontSize}
      gap={toPx(gapX)}
      h={h}
      onClick={() => onChange?.(!isSelected)}
      px={toPx(paddingX)}
      py={toPx(paddingY)}
      ref={ref}
      type="button"
      activeBg={bgPressed}
      hoverBg={bgHover}
      {...containerColors}
      {...rest}
    >
      {isString(icon) && <Icon className="vui-tagToggleIcon" name={icon} {...iconStyles} />}
      {label && (
        <T className="vui-tagToggleText" fontSize={fontSize}>
          {label}
        </T>
      )}
    </TagToggleBase>
  )
})

TagToggle.displayName = 'TagToggle'

export default TagToggle
